/**
 * Link della demo privata di un progetto (ADR 0050 §3).
 *
 * La demo non si apre dal suo indirizzo: serve un link che il gateway crea con la sessione
 * di chi lo chiede. Il link porta il token nel frammento (`#k=…`), che non arriva ai
 * server né ai log, e vale finché vale la sessione da cui è nato. Chi lo riceve vede la
 * demo e basta: il token non apre la dashboard.
 *
 * Un link nuovo non revoca i vecchi; «Revoca i link» li toglie tutti, per ogni sessione
 * dell'utente.
 *
 * Provvisorio dichiarato, come gli altri client (CLAUDE.md §2.3): percorsi e tipi scritti
 * a mano, presidiati da `demoLinks.test.ts`.
 */

import type { ApiClient } from '../client';

/** Un link della demo appena creato. */
export interface DemoLink {
  /** Indirizzo da aprire, con il token nel frammento. */
  url: string;
  /** Scadenza massima, ISO 8601: la fine della sessione. */
  expires_at: string;
}

/** Percorso dei link della demo nel gateway. */
const LINKS_PATH = '/api/v1/demo-sites/links';

/** Operazioni sui link della demo. */
export class DemoLinksApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Crea un link della demo del progetto.
   *
   * @param setId Set da cui si guarda il progetto: il gateway ne legge l'hosting.
   * @param projectId Progetto.
   * @returns Il link e la sua scadenza.
   * @throws ApiError 409 `demo_not_ready` se la demo non è pronta.
   */
  create(setId: string, projectId: string): Promise<DemoLink> {
    return this.client.request<DemoLink>('POST', LINKS_PATH, { agent_set_id: setId, project_id: projectId });
  }

  /**
   * Revoca tutti i link della demo creati dall'utente, da qualunque sessione.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @returns Quanti link sono stati revocati.
   */
  async revoke(setId: string, projectId: string): Promise<number> {
    const query = new URLSearchParams({ agent_set_id: setId, project_id: projectId });
    const result = await this.client.request<{ revoked: number }>('DELETE', `${LINKS_PATH}?${query.toString()}`);
    return result.revoked;
  }
}
